import ScrollUpArrow from "@/components/atoms/ScrollUpArrow";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import { useI18nContext } from "@/hooks/useI18nContext";
import {
  COMPANY_LEGAL_NAME,
  COMPANY_LEGAL_NAME_US,
} from "@/shared/constants/common.constants";
import Amplitude from "@/shared/services/amplitude.service";
import { useDataStore } from "@/shared/store/useDataStore";
import {
  getCurrentURL,
  getReferrer,
  getScreenWidth,
} from "@/shared/utils/commons";
import { getFromLocal } from "@/shared/utils/storage.util";
import { getUTMFieldsFromLocalStorage } from "@/shared/utils/utm.util";
import { Typography } from "@graphy/ui";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";

export const ROOT_HOST_NEW = `${process.env.NEXT_PUBLIC_ROOT_HOST}` || "https://graphy.com";

export type FooterDataType = {
  sectionTitle: string;
  sectionItems: {
    label: string;
    url?: string;
  }[];
}[];

const Footer = ({ footerData }: { footerData: FooterDataType }) => {
  const router = useRouter();

  const utmParams = getFromLocal("utmFields");
  const path = usePathname();
  const isUsLocal = useI18nContext().locale === "us";
  // After flattening URLs, "/page/<slug>" no longer exists — the CMS branch of
  // [slug].tsx publishes the active marketing slug into the data store so the
  // footer can still gate locale-specific contact info on it.
  const marketingPageSlug = useDataStore((s) => s.marketingPageSlug);
  const isUsLocalePage = useI18nContext().locale === "us" && !!marketingPageSlug;
  const isEnLocalePage = useI18nContext().locale === "en" && !!marketingPageSlug;


const showContact = isUsLocalePage && (marketingPageSlug.includes('online-courses') || marketingPageSlug.includes('partners'));

const showEmailForPartners = isEnLocalePage && marketingPageSlug.includes('partners');



  const contactViewUs = (
    <>
      {!showContact && <p className="text-body-10 font-normal text-grey-4">+1 (337) 595-6025</p>}
      <p className="text-body-10 font-normal text-grey-4">team@graphy.com</p>
    </>
  );

  const contactViewEn = (
    <p className="text-body-10 font-normal text-grey-4">care@graphy.com</p>
  );

  const addressViewUs = (
    <>
      <p className="text-body-10 font-normal text-grey-4">
        Vistra (Delaware) Ltd, 3500 South Dupont HWY
      </p>
      <p className="text-body-10 font-normal text-grey-4">
        Dover, Kent, DE 19901
      </p>
    </>
  );

  const addressViewEn = (
    <>
      <p className="text-body-10 font-normal text-grey-4">
        Maruti Infotech Centre, 2nd Floor, B-Block,
      </p>

      <p className="text-body-10 font-normal text-grey-4">
        Domlur, Koramangala Inner Ring Road,
      </p>
      <p className="text-body-10 font-normal text-grey-4">
        Bangalore- 560 071, Karnataka, India.
      </p>
    </>
  );

  if (isUsLocalePage || isEnLocalePage) {
    return (
      <footer className=" w-full bg-purple-1 p-4 md:p-6 lg:py-12">
        <div className="md:page-max-width">
          <div className="flex flex-col items-start justify-between gap-10 sm:flex-row sm:gap-0">
            <div className="flex w-full justify-center sm:justify-start">
              <div>
                <Image
                  src="/images/graphy_logo_black.svg"
                  width={100}
                  height={100}
                  alt="graphy"
                  className="mx-auto sm:mx-0"
                />
                <Typography
                  fontSize={"text-footnote"}
                  colorClass="text-purple-4 mt-3"
                  fontWeight={"font-normal"}
                >
                  © {new Date().getFullYear()} {isUsLocalePage ? COMPANY_LEGAL_NAME_US :  COMPANY_LEGAL_NAME }
                </Typography>
              </div>
            </div>

            <div className="flex w-full flex-col items-start gap-10 sm:flex-row sm:gap-16">
              <div className="w-full text-center sm:text-left">
                <p className="mb-2 text-title-10 font-bold text-grey-4">
                  CONTACT US
                </p>
                {isUsLocalePage ? contactViewUs : contactViewEn}
              </div>
              <div className="w-full text-center sm:text-left">
                <p className="mb-2 text-title-10 font-bold text-grey-4">
                  ADDRESS
                </p>
                <p className="text-body-10 font-normal text-grey-4">
                  {isUsLocalePage ? COMPANY_LEGAL_NAME_US :  COMPANY_LEGAL_NAME }
                </p>
                {isUsLocalePage ? addressViewUs : addressViewEn}
              </div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  

  return (
    <footer className=" w-full bg-purple-1 p-4 md:p-6 lg:py-12">
      <div className="md:page-max-width">
        <div className="grid w-full grid-cols-3 gap-3 md:gap-6 lg:grid-cols-5">
          {footerData.map((item, index) => {
            let footerLinkProps: {
              target: string;
              rel?: string;
            } = {
              target: "_blank",
            };

            if (item.sectionTitle !== "Graphy as a") {
              footerLinkProps["rel"] = "nofollow";
            }
            return (
              <div
                key={item.sectionTitle}
                className={`${
                  index > 2
                    ? "flex flex-col lg:self-start lg:justify-self-end"
                    : ""
                }`}
              >
                <Typography
                  fontWeight={"font-extrabold"}
                  fontSize={"text-footnote"}
                  className="pb-3 uppercase"
                  colorClass="text-purple-4"
                >
                  {item.sectionTitle}
                </Typography>
                {item?.sectionItems.map((sectionItem, index) => {
                  return (
                    sectionItem?.url && (
                      <Link
                        href={sectionItem?.url}
                        key={sectionItem.label}
                        {...footerLinkProps}
                        onClick={() => {
                          const {
                            current_url,
                            screen_width,
                            referrer,
                            section_name,
                            item_name,
                            utm_params,
                          } =
                            LANDING_PAGE_EVENTS.FOOTER_ITEM_TAPPED.PROPERTIES;
                          new Amplitude().sendEvent({
                            type: LANDING_PAGE_EVENTS.FOOTER_ITEM_TAPPED
                              .LABEL,
                            properties: {
                              [current_url]: getCurrentURL(),
                              [screen_width]: getScreenWidth(),
                              [referrer]: getReferrer(),
                              [utm_params]: getUTMFieldsFromLocalStorage(),
                              [section_name]: item?.sectionTitle,
                              [item_name]: sectionItem?.label,
                            } as { [key: string]: string },
                          });
                        }}
                      >
                        <p className="pb-2 text-body-12 text-grey-4 md:text-body-14">
                          {" "}
                          {sectionItem.label}{" "}
                        </p>
                      </Link>
                    )
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="md: mt-8 flex flex-row-reverse justify-between md:flex-row">
          <div>
            <Link href={ROOT_HOST_NEW}>
              <Image
                src="/images/graphy_logo_black.svg"
                width={100}
                height={100}
                alt="graphy"
              />
            </Link>
            <Typography
              fontSize={"text-footnote"}
              colorClass="text-purple-4 mt-3"
              fontWeight={"font-normal"}
            >
              © {new Date().getFullYear()}{" "}
              {isUsLocal ? COMPANY_LEGAL_NAME_US : COMPANY_LEGAL_NAME}
            </Typography>
          </div>
          <ScrollUpArrow />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
